import {IUser} from '../../models/auth';
import {SubscriptionDTO} from '../../dtos/billing';
import {addDays, isBefore as _isBefore} from 'date-fns';
import {IClinic} from '../../models/clinic';

export interface IResponseSubscriptionValidation {
  valid: boolean;
  message: string;
  userType?: 'expert' | 'patient' | 'admin';
  route?: string[];
}

export class ValidationSubscription {
  user: IUser;
  subscription: SubscriptionDTO;
  clinic: IClinic;
  public constructor(user: IUser, subscription: SubscriptionDTO, clinic: IClinic) {
    this.user = user;
    this.subscription = subscription;
    this.clinic = clinic;
  }

  checkSubscriptionPastDue(): IResponseSubscriptionValidation {
    if (!this.clinic || !this.user || !this.subscription) {
      return {
        valid: false,
        message: 'Ha ocurrido un error, por favor intente más tarde.',
        route: ['/', 'auth/new/landing'],
      };
    }

    // [`clinic/${paramClinicId}/plans/select`]

    const userType = this.getUserType();

    if (this.subscription.status === 'active') {
      return {
        valid: true,
        message: 'Suscripción activa',
      };
    }

    // Validación para cuando la suscripción está en modo de prueba
    if (this.subscription.status === 'trialing') {
      // Se le agregan 10 días para que pueda pagar la suscripción
      const isAValidPeriod = _isBefore(new Date(), addDays(new Date(this.subscription.trialEnd), 10));
      return {
        valid: isAValidPeriod,
        message: isAValidPeriod ? undefined : 'La suscripción se encuentra vencida.',
        userType,
        route: userType === 'admin' ? [`clinic/${this.clinic._id}/plans/select`] : ['/', 'auth/new/landing'],
      };
    }

    // Validación para cuando la suscripción está cancelada
    if (this.subscription.status === 'canceled') {
      // Se le agregan 10 días para que pueda pagar la suscripción
      const isAValidPeriod = _isBefore(new Date(), addDays(new Date(this.subscription.currentPeriodEnd), 10));
      let route: string[] = ['auth', 'not-allowed-access'];
      if (this.subscription.isBeta && userType === 'admin') {
        route = [`clinic/${this.clinic._id}/plans/beta`];
      } else if (!this.subscription.isBeta && userType === 'admin') {
        route = [`clinic/${this.clinic._id}/plans/select`];
      }
      return {
        valid: isAValidPeriod,
        message: isAValidPeriod ? undefined : 'La suscripción se encuentra cancelada.',
        userType,
        route,
      };
    }

    if (this.subscription.status === 'past_due') {
      // Si no es admin se rechaza el acceso a la plataforma
      if (userType !== 'admin') {
        return {
          valid: false,
          message: 'La suscripción se encuentra vencida.',
          userType,
          route: ['/', 'auth/new/landing'],
        };
      }

      // Se agregan 10 días para que el administrador pueda pagar la suscripción de nuevo
      const isAValidPeriod = _isBefore(new Date(), addDays(new Date(this.subscription.currentPeriodEnd), 10));

      return {
        valid: isAValidPeriod,
        message: isAValidPeriod ? undefined : 'La suscripción se encuentra vencida',
        userType,
        route: [`clinic/${this.clinic._id}/plans/select`],
      };
    }

    return {
      valid: false,
      message: 'Ha ocurrido un error, por favor intente más tarde.',
      userType,
      route: ['/', 'auth/new/landing'],
    };
  }

  checkExceedPatients(): IResponseSubscriptionValidation {
    const response: IResponseSubscriptionValidation = {
      valid: true,
      message: '',
      userType: this.getUserType()
    };

    if (this.subscription.metadata.totalPatients > this.subscription.metadata.patients) {
      // ya se ha excedido de pacientes activos, se verifica la fecha de la última cita
      const accessDate = _isBefore(new Date(), addDays(new Date(this.subscription.metadata.lastAppointment.createdAt), 2));
      response.valid = !accessDate;
      response.message = accessDate ? 'Se ha excedido el número de pacientes activos que permite el plan actual' : '';
    }
    return response;
  }

  private getUserType(): 'expert' | 'patient' | 'admin' {
    let response;
    try {
      if (this.user.patients) {
        response = 'patient';
      } else if ((this.user.experts && this.clinic.ownerId !== this.user._id)) {
        response = 'expert';
      } else {
        response = 'admin';
      }
    } catch (e) {
      console.log(e);
    }
    return response;
  }
}
